import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Upload, X, Star } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";

export default function ImageUploader({ images, onChange, max = 6 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/upload", form);
      onChange([...images, r.data.path].slice(0, max));
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(formatApiError(e, "Envoi impossible"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const move = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div data-testid="image-uploader">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {images.map((img, i) => (
          <div key={img} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
            {i === 0 && (
              <span className="absolute left-1 top-1 flex items-center gap-1 rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase text-ink">
                <Star className="h-2.5 w-2.5" /> Principale
              </span>
            )}
            <div className="absolute inset-x-1 bottom-1 flex justify-between">
              <div className="flex gap-1">
                <button type="button" data-testid={`img-move-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0}
                  className="rounded-full bg-ink/80 p-1 text-white disabled:opacity-30"><ChevronLeft className="h-3 w-3" /></button>
                <button type="button" data-testid={`img-move-right-${i}`} onClick={() => move(i, 1)} disabled={i === images.length - 1}
                  className="rounded-full bg-ink/80 p-1 text-white disabled:opacity-30"><ChevronRight className="h-3 w-3" /></button>
              </div>
              <button type="button" data-testid={`img-delete-${i}`} onClick={() => onChange(images.filter((x) => x !== img))}
                className="rounded-full bg-red-600/90 p-1 text-white"><X className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {images.length < max && (
          <button type="button" data-testid="img-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-gold hover:text-gold-dark">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            <span className="text-[10px] font-semibold">{uploading ? "Envoi…" : "Ajouter"}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} data-testid="img-file-input" type="file" accept="image/*" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}
